import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface PopoverProps {
  open: boolean;
  onClose: () => void;
  /** The element the panel anchors to. */
  anchorRef: React.RefObject<HTMLElement | null>;
  children: React.ReactNode;
  /** Horizontal alignment relative to the anchor. */
  align?: 'start' | 'end';
  /** Panel width in px; defaults to content width with a sensible minimum. */
  width?: number;
  className?: string;
  /** Accessible label for the panel. */
  'aria-label'?: string;
}

const GAP = 6;

/**
 * Desktop popover anchored to its trigger. Replaces the bottom sheet on wide
 * screens: a small panel next to the control that opened it, not a modal in
 * the middle of a 1440px canvas. Rendered in a portal with fixed positioning
 * so it escapes any overflow-hidden ancestor (tables, cards).
 *
 * Closes on outside click and Escape, flips above the anchor when there is no
 * room below, and returns focus to the trigger when closed by keyboard.
 */
export function Popover({ open, onClose, anchorRef, children, align = 'start', width, className = '', ...aria }: PopoverProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  // Fixed from the first paint: measured in normal flow the panel would span
  // the whole body width and every placement would clamp to the left edge.
  const [style, setStyle] = useState<React.CSSProperties>({ position: 'fixed', top: 0, left: 0, width: width ?? undefined, visibility: 'hidden' });

  const place = useCallback(() => {
    const anchor = anchorRef.current;
    const panel = panelRef.current;
    if (!anchor || !panel) return;
    const a = anchor.getBoundingClientRect();
    const p = panel.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let top = a.bottom + GAP;
    if (top + p.height > vh - 8 && a.top - GAP - p.height > 8) {
      top = a.top - GAP - p.height;
    }
    let left = align === 'end' ? a.right - p.width : a.left;
    left = Math.max(8, Math.min(left, vw - p.width - 8));

    setStyle({ position: 'fixed', top, left, width: width ?? undefined, visibility: 'visible' });
  }, [anchorRef, align, width]);

  useLayoutEffect(() => {
    if (!open) {
      setStyle({ position: 'fixed', top: 0, left: 0, width: width ?? undefined, visibility: 'hidden' });
      return;
    }
    place();
  }, [open, place, width]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (panelRef.current?.contains(t) || anchorRef.current?.contains(t)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        anchorRef.current?.focus();
      }
    };
    const onScroll = () => place();
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onScroll);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onScroll);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open, onClose, anchorRef, place]);

  if (!open) return null;

  return createPortal(
    <div
      ref={panelRef}
      role="dialog"
      aria-label={aria['aria-label']}
      style={style}
      className={`z-50 min-w-[200px] rounded-ios-card border border-[var(--border-default)] bg-[var(--bg-surface)] shadow-ios-elevated animate-fade-in ${className}`}
    >
      {children}
    </div>,
    document.body
  );
}

export default Popover;
